// src/core/groups.js — grupos y jugadores contra Supabase Postgres.
//
// FASE 4 · F1.3.b. Reescrito: grupos y jugadores ya no viven en localStorage.
// Lo que el código viejo llamaba "memberships" es, en las specs canónicas, la
// tabla `jugadores` (no hay tabla `memberships` aparte). Una fila de
// `jugadores` ES una membership.
//
// Patrón de cache (igual que getCurrentUser en F1.2/F1.3.a): las funciones de
// LECTURA conservan su firma SÍNCRONA y leen del cache de db.js. El cache se
// hidrata async desde Postgres con `cargarMisGrupos()`, que el bootstrap
// (src/main.js) llama una vez tras el login y se re-llama tras crear/unirse.
// Como la RLS de `grupos` solo deja ver los grupos propios, el cache = "todos
// mis grupos" — no hace falta hidratación por-ruta.
//
// Escrituras: createGroup / joinGroup pasan por RPCs SECURITY DEFINER
// (`crear_grupo`, `unirse_a_grupo`) — necesarias porque la RLS de `grupos`
// oculta los grupos a los no-miembros, y unirse por código requiere resolver
// code→group_id como no-miembro.

import { supabase } from '../supabase.js'
import { getCurrentUser } from './auth.js'
import {
  setGrupoEnCache,
  limpiarGruposCache,
  getGrupoDeCache,
  getGruposCache,
} from './db.js'
import { buildGrupoShareUrl } from './constants.js'
import { queryWithTimeout } from '../utils/timeout.js'

export const MAX_GROUPS_PER_USER = 5

// Auditoría boot-hang (5-jun-2026): tope para los fetches de cargarMisGrupos y
// flag de "la última carga falló" (timeout/red) para que mis-grupos pueda
// ofrecer "Reintentar" en vez de mostrarse vacío engañosamente.
const GRUPOS_TIMEOUT_MS = 12000
let _gruposLoadFailed = false
export const gruposLoadFailed = () => _gruposLoadFailed

// ─── Adaptadores Postgres → shape de cache ─────────────────────────────────
// El cache mantiene aliases legacy (nombre, group_code, apodo, role…) para que
// los archivos no migrados (ranking.js, tribuna.js, perfil.js, rituales.js,
// components.js, pacto.js) sigan leyendo sin cambios.

const adaptarJugador = (j) => {
  const u = j.usuarios || {}
  return {
    player_id: j.player_id,
    user_id: j.user_id,
    group_id: j.group_id,
    display_name: j.display_name,
    known_as: j.known_as,
    is_admin: j.is_admin,
    es_editor_pacto: j.es_editor_pacto,
    joined_at: j.joined_at,
    last_seen_at: j.last_seen_at,
    nombre_real: u.nombre_real || null,
    avatar_url: u.avatar_url || null,
    // aliases legacy
    id: j.player_id,
    nombre: u.nombre_real || j.display_name,
    apodo: j.display_name,
    group_nickname: j.display_name,
    membership_id: `${j.group_id}_${j.user_id}`,
    role: j.is_admin ? 'owner' : 'member',
    status: 'active',
  }
}

const adaptarGrupo = (g, jugadores) => ({
  group_id: g.group_id,
  code: g.code,
  name: g.name,
  status: g.status,
  modo_certificacion: g.modo_certificacion,
  acepta_extranjeros: g.acepta_extranjeros,
  max_extranjeros: g.max_extranjeros,
  es_anfitrion: g.es_anfitrion,
  codigo_cupon_usado: g.codigo_cupon_usado,
  certificado_en: g.certificado_en,
  created_at: g.created_at,
  updated_at: g.updated_at,
  // aliases legacy
  group_code: g.code,
  nombre: g.name,
  jugadores: (jugadores || []).map(adaptarJugador),
})

// ─── Hidratación del cache desde Postgres ──────────────────────────────────

// Carga TODOS los grupos del usuario actual (con sus jugadores) al cache.
// La RLS garantiza que solo ve los suyos. Idempotente — limpia y recarga.
export const cargarMisGrupos = async () => {
  limpiarGruposCache()
  _gruposLoadFailed = false
  const user = getCurrentUser()
  if (!user) return []

  let mis
  try {
    mis = await queryWithTimeout(
      (signal) => supabase.from('jugadores').select('group_id').eq('user_id', user.user_id).abortSignal(signal),
      GRUPOS_TIMEOUT_MS, 'mis-jugadores',
    )
  } catch (err) { mis = { error: { message: err?.message || 'timeout' } } }
  if (mis.error) { console.error('[groups] cargarMisGrupos (jugadores):', mis.error.message); _gruposLoadFailed = true; return [] }
  const groupIds = (mis.data || []).map((j) => j.group_id)
  if (!groupIds.length) return []

  let grupos, jugadores
  try {
    [grupos, jugadores] = await Promise.all([
      queryWithTimeout((signal) => supabase.from('grupos').select('*').in('group_id', groupIds).abortSignal(signal), GRUPOS_TIMEOUT_MS, 'grupos'),
      queryWithTimeout((signal) => supabase.from('jugadores').select('*, usuarios(nombre_real, avatar_url)').in('group_id', groupIds).abortSignal(signal), GRUPOS_TIMEOUT_MS, 'jugadores'),
    ])
  } catch (err) { console.error('[groups] cargarMisGrupos timeout:', err?.message || err); _gruposLoadFailed = true; return [] }
  if (grupos.error) { console.error('[groups] cargarMisGrupos (grupos):', grupos.error.message); _gruposLoadFailed = true; return [] }
  if (jugadores.error) { console.error('[groups] cargarMisGrupos (jugadores 2):', jugadores.error.message); _gruposLoadFailed = true; return [] }

  const porGrupo = {}
  for (const j of jugadores.data || []) (porGrupo[j.group_id] = porGrupo[j.group_id] || []).push(j)
  for (const g of grupos.data || []) setGrupoEnCache(adaptarGrupo(g, porGrupo[g.group_id]))
  return grupos.data || []
}

// ─── Escrituras (vía RPCs SECURITY DEFINER) ────────────────────────────────

// Crea grupo + alta del creador como jugador admin/editor, atómico. El owner
// es auth.uid() (el RPC lo toma de la sesión). Devuelve { data } | { error }.
export const createGroup = async (nombre, apodoOwner) => {
  const { data, error } = await supabase.rpc('crear_grupo', {
    p_name: nombre,
    p_display_name: apodoOwner,
  })
  if (error) return { error }
  await cargarMisGrupos()
  return { data } // { group_id, code, name }
}

// Lookup publico de un grupo por codigo via RPC SECURITY DEFINER
// (get_group_preview, schema canonico §31.1, Bloque 3A). Accesible para anon
// y para authenticated. Devuelve { data } | { error }.
//   data: { nombre, codigo, jugadores_count, status } | null
// Usos: paso 1 de "Unirme con codigo" (renderIngreso, Bloque 6.3) y futuro
// carril del invitado (Bloque 9).
export const getGroupPreview = async (code) => {
  const { data, error } = await supabase.rpc('get_group_preview', {
    p_code: (code || '').toUpperCase().trim(),
  })
  if (error) return { error }
  const row = (data || [])[0]
  return { data: row || null }
}

// Une al usuario actual a un grupo por código. Devuelve { data } | { error }.
export const joinGroup = async (code, displayName) => {
  const { data, error } = await supabase.rpc('unirse_a_grupo', {
    p_code: code,
    p_display_name: displayName,
  })
  if (error) return { error }
  await cargarMisGrupos()
  return { data } // { group_id, code }
}

// INSERT directo de un jugador. La RLS (jugadores_insert CHECK user_id =
// auth.uid()) solo permite auto-insertarse. Las altas reales pasan por los
// RPCs; esto es un helper de bajo nivel.
export const addJugador = async (groupId, userId, displayName, isAdmin = false, esEditorPacto = false) => {
  const { data, error } = await supabase
    .from('jugadores')
    .insert({
      group_id: groupId,
      user_id: userId,
      display_name: displayName,
      is_admin: isAdmin,
      es_editor_pacto: esEditorPacto,
    })
    .select()
  if (error) return { error }
  return { data }
}

// ─── Lecturas async (Postgres) ─────────────────────────────────────────────

// SELECT de un grupo por código. La RLS solo lo devuelve si el usuario es
// miembro (o el grupo acepta extranjeros) — null en caso contrario.
export const getGroupByCode = async (code) => {
  const { data, error } = await supabase
    .from('grupos')
    .select('*')
    .eq('code', (code || '').toUpperCase())
    .maybeSingle()
  if (error) { console.error('[groups] getGroupByCode:', error.message); return null }
  return data || null
}

// Grupos de un usuario, vía jugadores JOIN grupos.
export const getGroupsForUser = async (userId) => {
  const { data, error } = await supabase
    .from('jugadores')
    .select('group_id, grupos(*)')
    .eq('user_id', userId)
  if (error) { console.error('[groups] getGroupsForUser:', error.message); return [] }
  return (data || []).map((row) => row.grupos).filter(Boolean)
}

// Jugadores de un grupo (por código), con nombre_real y avatar_url de usuarios.
export const getJugadoresDelGrupo = async (groupCode) => {
  const grupo = getGrupoDeCache(groupCode)
  const groupId = grupo?.group_id
  if (!groupId) return []
  const { data, error } = await supabase
    .from('jugadores')
    .select('*, usuarios(nombre_real, avatar_url)')
    .eq('group_id', groupId)
  if (error) { console.error('[groups] getJugadoresDelGrupo:', error.message); return [] }
  return (data || []).map(adaptarJugador)
}

// ─── Lecturas síncronas (cache — firma exterior preservada) ────────────────

export const getMembership = (codigo, userId) => {
  const uid = userId || getCurrentUser()?.user_id
  if (!codigo || !uid) return null
  const grupo = getGrupoDeCache(codigo)
  if (!grupo) return null
  return (grupo.jugadores || []).find((j) => j.user_id === uid) || null
}

export const getActiveMembershipsForUser = () => {
  // El cache solo contiene los grupos del usuario actual (RLS): cada grupo
  // cacheado es una membership suya. Se consume sobre todo `.length` para el
  // chequeo del límite de grupos.
  return Object.values(getGruposCache()).map((g) => ({
    group_id: g.group_id,
    code: g.code,
    status: 'active',
  }))
}

export const esUsuarioMiembroDelGrupo = (userId, groupCode) => {
  const grupo = getGrupoDeCache(groupCode)
  if (!grupo) return false
  return (grupo.jugadores || []).some((j) => j.user_id === userId)
}

export const contarJugadoresDelGrupo = (groupCode) => {
  const grupo = getGrupoDeCache(groupCode)
  return grupo ? (grupo.jugadores || []).length : 0
}

// ─── Sesión de grupo activo ────────────────────────────────────────────────

export const getActiveGroupCode = () => localStorage.getItem('grupo_activo')

export const setActiveGroupCode = (codigo) => {
  if (codigo) localStorage.setItem('grupo_activo', codigo.toUpperCase())
}

export const getGroupDisplayName = (grupo) => (grupo?.nombre || grupo?.name || 'MI GRUPO').toUpperCase()

export const getKnownGroups = () => {
  const activeCode = getActiveGroupCode()
  const all = Object.values(getGruposCache()).map((g) => ({ codigo: g.code, grupo: g }))
  const active = all.find((x) => x.codigo === activeCode)
  const rest = all.filter((x) => x.codigo !== activeCode)
  return (active ? [active, ...rest] : rest).slice(0, MAX_GROUPS_PER_USER)
}

export const hasValidActiveGroupSession = () => {
  const codigo = getActiveGroupCode()
  return Boolean(codigo && getGrupoDeCache(codigo))
}

export const getGroupContextRoute = (codigo) => {
  const hash = window.location.hash.slice(1)
  const contextualRoutes = [
    '/ranking/',
    '/tribuna/',
    '/tribuna-arengas/',
    '/tribuna-cabalas/',
    '/tribuna-promesas/',
    '/pacto/',
    '/perfil/',
  ]
  const currentRoute = contextualRoutes.find((route) => hash.startsWith(route))
  return currentRoute ? `${currentRoute}${codigo}` : `/g/${codigo}`
}

// ─── Handlers UI (consumidos por onclick inline → deben vivir en window) ───

export const switchActiveGroup = (codigo) => {
  setActiveGroupCode(codigo)
  closeGroupSelector()
  document.body.style.opacity = '0.72'
  setTimeout(() => { document.body.style.opacity = '' }, 160)
  window.location.hash = getGroupContextRoute(codigo)
}

export const closeGroupSelector = () => {
  document.querySelectorAll('#active-group-selector').forEach((selector) => selector.remove())
}

export const openGroupSelector = (codigo) => {
  closeGroupSelector()
  const grupos = getKnownGroups()
  const groupCards = grupos.map(({ codigo: groupCode, grupo: group }) => `
        <button class="active-group-option ${groupCode === codigo ? 'active' : ''}" type="button" onclick="switchActiveGroup('${groupCode}')">
            <strong>${groupCode === codigo ? '&#10003; ' : ''}${getGroupDisplayName(group)}</strong>
            <em><span>Código:</span> ${groupCode}</em>
            <small>${group.jugadores?.length || 0} ${(group.jugadores?.length || 0) === 1 ? 'jugador' : 'jugadores'}</small>
        </button>
    `).join('')

  const selector = document.createElement('div')
  selector.id = 'active-group-selector'
  selector.innerHTML = `
        <style>
            #active-group-selector{position:fixed;inset:0;z-index:9999;background:rgba(0,0,0,.76);backdrop-filter:blur(7px);display:flex;align-items:flex-end;justify-content:center}
            .group-sheet{width:100%;max-width:480px;min-height:76vh;max-height:86vh;overflow-y:auto;background:rgba(9,14,23,.99);border:1px solid rgba(255,255,255,.12);border-bottom:0;border-radius:18px 18px 0 0;padding:1rem 1rem 1.25rem;box-shadow:0 -18px 48px rgba(0,0,0,.44)}
            .group-sheet-head{display:flex;justify-content:space-between;align-items:center;margin-bottom:.65rem}.group-sheet-head strong{color:var(--text-muted);font-size:.78rem;text-transform:uppercase;letter-spacing:.04em}.group-sheet-head button{border:0;background:transparent;color:var(--text-muted);width:28px;height:28px;font-size:.9rem;cursor:pointer}
            .group-limit-note{color:var(--text-muted);font-size:.72rem;text-align:right;margin-bottom:.65rem}
            .active-group-option{width:100%;border:1px solid rgba(255,255,255,.09);background:rgba(255,255,255,.045);border-radius:10px;padding:.95rem .9rem;color:var(--white);text-align:left;margin-bottom:.72rem;cursor:pointer}
            .active-group-option.active{border-color:rgba(117,170,219,.42);background:rgba(117,170,219,.13);box-shadow:0 0 18px rgba(117,170,219,.1)}
            .active-group-option strong{display:block;font-size:.9rem;line-height:1.2;color:var(--white);font-weight:900}
            .active-group-option em{display:block;color:rgba(117,170,219,.86);font-style:normal;font-size:.74rem;margin-top:.3rem}.active-group-option em span{color:var(--text-muted)}
            .active-group-option small{display:block;color:var(--text-muted);font-size:.72rem;margin-top:.22rem}
        </style>
        <div class="group-sheet" role="dialog" aria-modal="true" aria-label="Mis grupos">
            <div class="group-sheet-head"><strong>Mis grupos</strong><button type="button" aria-label="Cerrar selector" onclick="closeGroupSelector()">X</button></div>
            <div class="group-limit-note">${grupos.length}/${MAX_GROUPS_PER_USER} grupos activos</div>
            ${groupCards || `<div style="color:var(--text-muted);font-size:.82rem;padding:.7rem;">Todavia no tenes grupos activos.</div>`}
        </div>
    `
  selector.addEventListener('click', (event) => {
    if (event.target === selector) closeGroupSelector()
  })
  document.body.appendChild(selector)
}

// Bloque 8.5: helpers puros del mensaje de invitacion. Exportados para test.
// El mensaje matchea LITERAL lo que pide v2.1 §3 (mismo formato que el
// shareActiveGroup historico).
export const buildInviteMessage = (nombre, codigo, link) =>
  `⚽ Unite a mi grupo en PACTO MUNDIAL: ${nombre}\n\nEl prode del Mundial 2026 entre amigos. Entrás con tu cuenta de Google, sin tarjeta ni datos raros.\n\nCódigo: ${codigo}\nEntrá acá: ${link}`

// Telegram concatena la URL del param `url` al final del `text` cuando comparte.
// Para evitar URL duplicada, el text NO incluye la linea de "Entrá acá".
export const buildTelegramShareUrl = (nombre, codigo) => {
  const fullUrl = buildGrupoShareUrl(codigo)
  const text = `⚽ Unite a mi grupo de PACTO MUNDIAL:\n\n${nombre}\n\nCódigo:\n${codigo}`
  return `https://t.me/share/url?url=${encodeURIComponent(fullUrl)}&text=${encodeURIComponent(text)}`
}

// ─── Modal "Compartir invitación" (Bloque 8.5 · refactor 27-may-2026) ──────
// El modal vivía inline acá; en el refactor del 27-may se extrajo a
// src/ui/share-modal.js (openShareModal genérico) para reusarlo desde
// arengas (grupo + Tribuna). openInviteModal queda como wrapper que arma
// el mensaje específico de invitación y delega al genérico.

import { openShareModal, closeShareModal } from '../ui/share-modal.js'

export const closeInviteModal = () => closeShareModal()

export const openInviteModal = (nombre, codigo) => {
  // El mensaje usa la URL COMPLETA (con https) y la pasamos también como
  // shareUrl: así openShareModal detecta que el texto ya incluye el link y NO
  // lo appendea de nuevo (evita el link duplicado en WhatsApp). El link viaja
  // clickeable una sola vez.
  const fullUrl = buildGrupoShareUrl(codigo)
  const mensaje = buildInviteMessage(nombre, codigo, fullUrl)
  openShareModal({
    title: 'Compartir invitación',
    subtitle: `${nombre} · ${codigo}`,
    shareUrl: fullUrl,
    shareText: mensaje,
  })
}

export const shareActiveGroup = (codigo) => {
  const grupo = getGrupoDeCache(codigo)
  if (!grupo) return
  const nombre = getGroupDisplayName(grupo)
  openInviteModal(nombre, codigo)
}

// ─── Bridge window.* — solo los 4 handlers que el onclick inline necesita ──

if (typeof window !== 'undefined') {
  window.switchActiveGroup = switchActiveGroup
  window.closeGroupSelector = closeGroupSelector
  window.openGroupSelector = openGroupSelector
  window.shareActiveGroup = shareActiveGroup
}
